import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NotificationBell } from './NotificationBell';
import { Button } from './ui/button';
import { LogOut, User, ChevronDown, Star } from 'lucide-react';

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileRef = useRef(null);

  // Close the profile dropdown when clicking outside.
  useEffect(() => {
    if (!profileMenuOpen) return;
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [profileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) return null;

  const getDashboardLink = () => {
    switch (user.role) {
      case 'admin': return '/admin';
      case 'driver': return '/driver';
      default: return '/customer';
    }
  };

  const getRoleBadge = () => {
    const colors = {
      admin: 'bg-[#2E7D32] text-white',
      driver: 'bg-[#F57C00] text-white',
      customer: 'bg-gray-600 text-white'
    };
    return (
      <span className={`px-2 py-1 text-xs uppercase tracking-wider font-bold ${colors[user.role]}`}>
        {user.role}
      </span>
    );
  };

  // Avatar initials for driver dropdown trigger
  const initials = (user.name || '?')
    .split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('') || '?';

  // Profile dropdown is shown for drivers (Profile + Reviews) and customers
  // (Settings). Both roles get a logout entry inside the menu. Admins keep
  // the standalone "Logout" button only.
  const showProfileMenu = user.role === 'driver' || user.role === 'customer';

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to={getDashboardLink()} 
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
            data-testid="navbar-logo"
          >
            <img src="/logo.png" alt="JustDeliver" className="h-[22px] sm:h-[30px] lg:h-[37px] w-auto" />
          </Link>

          {/* User Info & Actions */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3">
              <span className="text-sm text-gray-600">{user.name}</span>
              {getRoleBadge()}
            </div>
            
            <NotificationBell />

            {showProfileMenu && (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileMenuOpen(o => !o)}
                  className="flex items-center gap-1.5 rounded-full px-1.5 py-1 hover:bg-gray-100 transition-colors"
                  data-testid="navbar-profile-avatar"
                  aria-haspopup="menu"
                  aria-expanded={profileMenuOpen}
                >
                  <span className="w-8 h-8 rounded-full bg-[#2E7D32] text-white text-xs font-bold flex items-center justify-center">
                    {initials}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {profileMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50"
                    role="menu"
                    data-testid="navbar-profile-menu"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-bold text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate(user.role === 'customer' ? '/customer?view=settings' : '/driver?view=profile');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="navbar-profile-link"
                      role="menuitem"
                    >
                      <User className="w-4 h-4 text-gray-500" />
                      <span>{user.role === 'customer' ? 'Settings' : 'Bidding Profile & Settings'}</span>
                    </button>
                    {user.role === 'driver' && (
                    <button
                      type="button"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate('/driver?view=profile#reviews');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="navbar-reviews-link"
                      role="menuitem"
                    >
                      <Star className="w-4 h-4 text-gray-500" />
                      <span>My reviews</span>
                    </button>
                    )}
                    <button
                      type="button"
                      onClick={() => { setProfileMenuOpen(false); handleLogout(); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                      data-testid="navbar-profile-logout"
                      role="menuitem"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Log out</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {!showProfileMenu && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="gap-2 border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32] hover:text-white transition-colors"
                data-testid="navbar-logout-btn"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
